import type { TableCell, TableElement } from '@/components/canvas/document/publicTypes';
import { elementFillCss, paintToCss } from '@/components/canvas/elements/model';
import {
  TABLE_DEFAULT_TEXT_COLOR,
  TABLE_DEFAULT_VERTICAL_ALIGN,
} from '@/components/canvas/shared/constants/legacyConfig';

export function getRowHeights(element: TableElement): number[] {
  const { rows, rowHeights } = element.data;
  const { height } = element.size;
  if (rowHeights && rowHeights.length >= rows) return rowHeights.slice(0, rows);
  const h = rows > 0 ? Math.floor(height / rows) : 0;
  return Array.from({ length: rows }, () => h);
}

export function getColWidths(element: TableElement): number[] {
  const { cols, colWidths } = element.data;
  const { width } = element.size;
  if (colWidths && colWidths.length >= cols) return colWidths.slice(0, cols);
  const w = cols > 0 ? Math.floor(width / cols) : 0;
  return Array.from({ length: cols }, () => w);
}

/** 由行列尺寸数组得到与单元格网格一致的总宽高 */
export function tableLayoutSize(colWidths: number[], rowHeights: number[]): {
  width: number;
  height: number;
} {
  return {
    width: colWidths.reduce((a, b) => a + b, 0),
    height: rowHeights.reduce((a, b) => a + b, 0),
  };
}

/** 选框 / 八向手柄使用的表格外框（与网格 layout 一致） */
export function getTableChromeRect(
  element: TableElement,
  offset: { dx: number; dy: number } = { dx: 0, dy: 0 },
): { x: number; y: number; width: number; height: number } {
  const { width, height } = tableLayoutSize(getColWidths(element), getRowHeights(element));
  return {
    x: element.layout.x + offset.dx,
    y: element.layout.y + offset.dy,
    width,
    height,
  };
}

export function getCell(element: TableElement, r: number, c: number): TableCell | undefined {
  return element.data.cells?.[`${r},${c}`];
}

export function getCellBackground(element: TableElement, r: number, c: number): string {
  const cell = getCell(element, r, c);
  return (
    paintToCss(cell?.appearance?.fills?.[0]) ??
    paintToCss(element.data.rowBackgrounds?.[String(r)]?.[0]) ??
    paintToCss(element.data.colBackgrounds?.[String(c)]?.[0]) ??
    elementFillCss(element) ??
    '#fff'
  );
}

export function resolveCellTextColor(element: TableElement, cell: TableCell | undefined): string {
  return (
    cell?.textStyle?.typography?.color ??
    element.typography.color ??
    TABLE_DEFAULT_TEXT_COLOR
  );
}

export type CellTextStyle = {
  fontSize: number;
  fontWeight: number | string;
  fontFamily: string;
  fontStyle: 'normal' | 'italic';
  color: string;
  lineHeight?: number | string;
  letterSpacing?: number;
  textAlign: 'left' | 'center' | 'right' | 'justify';
  verticalAlign: 'top' | 'middle' | 'bottom';
  writingMode?: 'horizontal-tb' | 'vertical-rl';
};

/** 解析指定单元格的完整文字样式，依次从单元格覆盖 → 表格默认 → 系统默认 */
export function getCellTextStyle(element: TableElement, r: number, c: number): CellTextStyle {
  const cell = getCell(element, r, c);
  const s = cell?.textStyle ?? {};
  const typography = s.typography ?? {};
  const paragraph = s.paragraph ?? {};
  return {
    fontFamily: typography.fontFamily ?? element.typography.fontFamily,
    fontSize: typography.fontSize ?? element.typography.fontSize,
    fontWeight: typography.fontWeight ?? element.typography.fontWeight,
    fontStyle: (typography.fontStyle ?? element.typography.fontStyle ?? 'normal') as 'normal' | 'italic',
    color: typography.color ?? element.typography.color,
    lineHeight: typography.lineHeight ?? element.typography.lineHeight,
    letterSpacing: typography.letterSpacing ?? element.typography.letterSpacing,
    textAlign: paragraph.textAlign ?? element.paragraph.textAlign,
    verticalAlign: paragraph.verticalAlign ?? element.paragraph.verticalAlign ?? TABLE_DEFAULT_VERTICAL_ALIGN,
    writingMode: paragraph.writingMode ?? element.paragraph.writingMode,
  };
}

/** 若 (r,c) 落在合并区内且非左上角，返回 null；否则返回 span 信息。 */
export function getMergedInfo(
  element: TableElement,
  r: number,
  c: number,
): { rowSpan: number; colSpan: number; originR: number; originC: number } | null {
  const ranges = element.data.mergedRanges ?? [];
  for (const [r0, c0, r1, c1] of ranges) {
    if (r >= r0 && r <= r1 && c >= c0 && c <= c1) {
      if (r === r0 && c === c0) {
        return { rowSpan: r1 - r0 + 1, colSpan: c1 - c0 + 1, originR: r0, originC: c0 };
      }
      return null;
    }
  }
  return { rowSpan: 1, colSpan: 1, originR: r, originC: c };
}

export function moveIndex<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
  const out = arr.slice();
  const [rem] = out.splice(fromIndex, 1);
  out.splice(toIndex, 0, rem);
  return out;
}

export function reorderColumns(
  element: TableElement,
  colWidths: number[],
  fromCol: number,
  toCol: number,
): { colWidths: number[]; cells: Record<string, TableCell> } {
  const cols = colWidths.length;
  const order = moveIndex(
    Array.from({ length: cols }, (_, i) => i),
    fromCol,
    toCol,
  );
  const newWidths = moveIndex(colWidths, fromCol, toCol);
  const cells: Record<string, TableCell> = {};
  const rows = Math.max(1, element.data.rows);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const oldC = order[c];
      const cell = getCell(element, r, oldC);
      if (cell) cells[`${r},${c}`] = cell;
    }
  }
  return { colWidths: newWidths, cells };
}

export function reorderRows(
  element: TableElement,
  rowHeights: number[],
  fromRow: number,
  toRow: number,
): { rowHeights: number[]; cells: Record<string, TableCell> } {
  const rows = rowHeights.length;
  const order = moveIndex(
    Array.from({ length: rows }, (_, i) => i),
    fromRow,
    toRow,
  );
  const newHeights = moveIndex(rowHeights, fromRow, toRow);
  const cells: Record<string, TableCell> = {};
  const cols = Math.max(1, element.data.cols);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const oldR = order[r];
      const cell = getCell(element, oldR, c);
      if (cell) cells[`${r},${c}`] = cell;
    }
  }
  return { rowHeights: newHeights, cells };
}

export function sumSlice(values: number[], start: number, end: number): number {
  return values.slice(start, end).reduce((a, b) => a + b, 0);
}
